// src/components/Services/AddCategoryModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import sericesApi from "../../api/service/service.api";
import Uploader from "../Uploader";
import ProcessingBar2 from "../ProcessingBar/ProcessingBar2";

const COLOR_OPTIONS = [
  { label: "Blue", value: "#93C5FD" },
  { label: "Red", value: "#FCA5A5" },
  { label: "Green", value: "#86EFAC" },
  { label: "Yellow", value: "#FDE68A" },
  { label: "Purple", value: "#D8B4FE" },
  { label: "Teal", value: "#99F6E4" },
  { label: "Gray", value: "#CBD5E1" },
];

export default function AddCategoryModal({
  open,
  onClose,
  onCreated, // called after create
  onUpdated, // called after update
  category, // if provided => edit mode
  operatorId,
}) {
  const isEdit = !!category?._id;

  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [colorOpen, setColorOpen] = useState(false);
  const [selectedColor, setSelectedColor] = useState(COLOR_OPTIONS[0]);
  const [loading, setLoading] = useState(false);

  const [Images, setImages] = useState([]);

  // Prefill when editing or when modal opens
  useEffect(() => {
    if (open) {
      if (isEdit) {
        setName(category?.name || "");
        setDesc(category?.description || "");
        const match = COLOR_OPTIONS.find(
          (c) => c.value === category?.color
        ) || {
          label: "Custom",
          value: category?.color || COLOR_OPTIONS[0].value,
        };
        setSelectedColor(match);
        // ✅ Prefill category image if it exists
        if (category?.image) {
          setImages([category.image]);
        } else {
          setImages([]);
        }
      } else {
        setName("");
        setDesc("");
        setSelectedColor(COLOR_OPTIONS[0]);
      }
    }
  }, [open, isEdit, category]);

  const disabled = useMemo(() => !name.trim() || loading, [name, loading]);

  const submit = async (e) => {
    e.preventDefault();
    if (disabled) return;

    const payload = {
      name: name.trim(),
      color: selectedColor.value, // hex
      description: desc.trim(),
      operator: operatorId,
      image: Images[0] || "", // ✅ include uploaded or existing image
    };

    setLoading(true);
    try {
      if (isEdit) {
        const { response, err } = await sericesApi.updateServiceCategory({
          id: category._id,
          ...payload,
        });
        if (err) throw err;
        const updatedCat =
          response?.data?.category || response?.category || response;
        onUpdated?.(updatedCat);
        toast.success("Category updated");
      } else {
        const { response, err } = await sericesApi.createServiceCategory(
          payload
        );
        if (err) throw err;
        const createdCat =
          response?.data?.category || response?.category || response;
        onCreated?.(createdCat);
        toast.success("Category created");
      }
      onClose?.();
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        (isEdit ? "Failed to update category" : "Failed to create category");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const updateImages = (url) => {
    setImages([url]);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={() => !loading && onClose?.()}
      />
      {/* Dialog */}
      <div className="relative z-10 w-[92vw] max-w-xl rounded-2xl bg-dry p-6 shadow-xl border border-redlight">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-text">
            {isEdit ? "Edit category" : "Add category"}
          </h3>
          <button
            type="button"
            className="p-2 rounded hover:bg-textGray"
            onClick={() => !loading && onClose?.()}
            aria-label="Close"
          >
            <X className="h-7 w-7 text-text bg-drylight rounded-lg p-1" />
          </button>
        </div>

        {/* Body */}
        <form className="mt-5 space-y-5" onSubmit={submit}>
          {/* Row: Name + Color */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Category name */}
            <div>
              <label className="block text-sm font-medium text-text mb-1">
                Category name
              </label>
              <input
                type="text"
                placeholder="e.g. Hair Services"
                className="w-full text-text rounded-lg border border-border bg-dry px-3 py-2 outline-none focus:ring-2 focus:ring-red-400"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {/* Appointment color */}
            <div className="relative">
              <label className="block text-sm font-medium text-text mb-1">
                Appointment color
              </label>
              <button
                type="button"
                className="w-full flex items-center justify-between rounded-lg border border-border px-3 py-2 hover:bg-gray-50"
                onClick={() => setColorOpen((s) => !s)}
              >
                <span className="flex items-center gap-2">
                  <span
                    className="inline-block h-5 w-5 rounded-full border border-black/10"
                    style={{ background: selectedColor.value }}
                  />
                  <span className="text-text">{selectedColor.label}</span>
                </span>
                <svg
                  className={`h-4 w-4 text-gray-600 transition-transform ${
                    colorOpen ? "rotate-180" : ""
                  }`}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {colorOpen && (
                <div className="absolute z-20 mt-1 w-full rounded-lg border border-border bg-dry shadow">
                  {COLOR_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        setSelectedColor(opt);
                        setColorOpen(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 hover:bg-border"
                    >
                      <span
                        className="inline-block h-5 w-5 rounded-full border border-black/10"
                        style={{ background: opt.value }}
                      />
                      <span className="text-sm text-text">{opt.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-text mb-1">
              Category Photo
            </label>
            <Uploader refreshFunction={updateImages} previewImage={Images[0]} />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">
              Description
            </label>
            <textarea
              rows={4}
              maxLength={255}
              className="w-full rounded-lg border border-border bg-dry px-3 py-2 outline-none focus:ring-2 focus:ring-red-400"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="category description..."
            />
            <div className="mt-1 text-right text-xs text-text">
              {desc.length}/255
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              className="rounded-lg border border-border px-4 py-2 text-text hover:bg-redlight"
              onClick={() => !loading && onClose?.()}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={disabled}
              className={`rounded-lg px-5 py-2 text-white min-w-[110px] hover:text-redlight ${
                disabled
                  ? "bg-dry cursor-not-allowed "
                  : "bg-redlight hover:bg-dry"
              }`}
            >
              {loading ? (
                isEdit ? (
                  <div className=" ml-8 my-2">
                    <ProcessingBar2 />
                  </div>
                ) : (
                  <div className="ml-8 my-2">
                    <ProcessingBar2 />
                  </div>
                )
              ) : isEdit ? (
                "Save"
              ) : (
                "Add"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
